import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-4c60e95a"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "bpmn-container" }
const _hoisted_2 = { class: "button-container" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = ["disabled"]

import { ref, onMounted, onBeforeUnmount } from 'vue';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import { BpmnPropertiesPanelModule, BpmnPropertiesProviderModule } from 'bpmn-js-properties-panel';
import CustomPropertiesProvider from './CustomPropertyProvider';

// Import required CSS
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css';


export default /*#__PURE__*/_defineComponent({
  __name: 'BpmnModeler',
  props: {
    processorService: {
        type: Object,
        required: true
    },
    inlineMode: {
        type: Boolean,
        default: false
    },
    hasUnsavedChanges: {
        type: Boolean,
        default: false
    }
},
  emits: ['nodeSelected', 'nodeDeleted', 'save', 'model-changed', 'factory-reset'],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const canvas = ref<HTMLDivElement | null>(null);
let modeler: BpmnModeler | null = null;

const currentXML = ref('');
const canFactoryReset = ref(true); // Always enabled since it's a factory reset

/**
 * Check if the XML contains meaningful process elements
 */
const hasMeaningfulProcessElements = (xml: string): boolean => {
    // Check for common BPMN elements
    const hasStartEvent = xml.includes('<bpmn:startEvent');
    const hasEndEvent = xml.includes('<bpmn:endEvent');
    const hasTask = xml.includes('<bpmn:task') || xml.includes('<bpmn:userTask') || 
                    xml.includes('<bpmn:serviceTask') || xml.includes('<bpmn:scriptTask');
    const hasGateway = xml.includes('<bpmn:exclusiveGateway') || xml.includes('<bpmn:parallelGateway') || 
                      xml.includes('<bpmn:inclusiveGateway');
    const hasSequenceFlow = xml.includes('<bpmn:sequenceFlow');
    
    // Log the results
    console.log('[BpmnModeler] XML analysis:');
    console.log('- Has start event:', hasStartEvent);
    console.log('- Has end event:', hasEndEvent);
    console.log('- Has task:', hasTask);
    console.log('- Has gateway:', hasGateway);
    console.log('- Has sequence flow:', hasSequenceFlow);
    
    // Return true if at least one meaningful element is found
    return hasStartEvent || hasEndEvent || hasTask || hasGateway || hasSequenceFlow;
};

/**
 * Load a BPMN diagram from the given XML string.
 */
const loadDiagram = async (xml: string) => {
    if (!modeler) return;
    try {
        console.log('[BpmnModeler] Attempting to load BPMN XML (length:', xml.length, ')');
        // Only log a small preview of the XML to avoid console clutter
        if (xml.length > 0) {
            console.log('[BpmnModeler] XML preview:', xml.substring(0, 100) + (xml.length > 100 ? '...' : ''));
            
            // Check if the XML contains a diagram element
            const hasDiagram = xml.includes('<bpmndi:BPMNDiagram');
            console.log('[BpmnModeler] XML contains diagram element:', hasDiagram);
            
            // Check if the XML contains a process element
            const hasProcess = xml.includes('<bpmn:process');
            console.log('[BpmnModeler] XML contains process element:', hasProcess);
            
            // Check if the XML contains any task elements
            const hasTasks = xml.includes('<bpmn:task') || xml.includes('<bpmn:userTask') || 
                            xml.includes('<bpmn:serviceTask') || xml.includes('<bpmn:scriptTask');
            console.log('[BpmnModeler] XML contains task elements:', hasTasks);
            
            // Check if the XML contains meaningful process elements
            const hasMeaningfulElements = hasMeaningfulProcessElements(xml);
            console.log('[BpmnModeler] XML contains meaningful process elements:', hasMeaningfulElements);
            
            if (!hasDiagram) {
                console.warn('[BpmnModeler] XML does not contain a diagram element (<bpmndi:BPMNDiagram>)');
                // Create a new diagram instead of trying to import the XML
                console.log('[BpmnModeler] Creating new diagram instead of importing XML');
                
                // First try to import the XML to get the process definitions
                try {
                    console.log('[BpmnModeler] Attempting to import XML to get process definitions');
                    await modeler.importXML(xml);
                    console.log('[BpmnModeler] Successfully imported XML with process definitions');
                } catch (importErr) {
                    console.error('[BpmnModeler] Error importing XML for process definitions:', importErr);
                    // Continue with creating a new diagram
                }
                
                // Create a new diagram
                modeler.createDiagram();
                console.log('[BpmnModeler] Created new diagram');
                
                // Log the elements in the diagram
                const elementRegistry = modeler.get('elementRegistry') as any;
                const elements = elementRegistry.getAll();
                console.log('[BpmnModeler] Elements in diagram:', elements.length);
                elements.forEach((element: any) => {
                    console.log(`[BpmnModeler] Element: ${element.id}, Type: ${element.type}`);
                });
                
                return;
            }
        } else {
            console.log('[BpmnModeler] XML is empty');
        }
        await modeler.importXML(xml);
        (modeler.get('canvas') as any).zoom('fit-viewport');
    } catch (err) {
        console.error('Error importing BPMN XML:', err);
        // Log more details about the error
        if (err instanceof Error) {
            console.error('[BpmnModeler] Error details:', err.message);
            console.error('[BpmnModeler] Error stack:', err.stack);
        }
        // Fallback if XML could not be loaded
        console.log('[BpmnModeler] Creating new diagram as fallback');
        modeler.createDiagram();
    }
};

onMounted(async () => {
    console.log('[BpmnModeler] Component mounted, initializing...');
    try {
        console.log('[BpmnModeler] Fetching process model XML from server...');
        const response = await props.processorService.getProcessModelXml();
        console.log('[BpmnModeler] Received response from server (XML length:', response.processModelXml?.length || 0, ')');
        currentXML.value = response.processModelXml ?? '';
        console.log('[BpmnModeler] Set currentXML (length:', currentXML.value.length, ')');
    } catch (error) {
        console.error('Error fetching BPMN XML:', error);
        currentXML.value = '';
    }

    console.log('[BpmnModeler] Creating BPMN modeler instance...');
    modeler = new BpmnModeler({
        container: canvas.value!,
        additionalModules: [
            BpmnPropertiesPanelModule,
            BpmnPropertiesProviderModule,
            {
                __init__: ['customPropertiesProvider'],
                customPropertiesProvider: ['type', CustomPropertiesProvider, ['$inject', ['propertiesPanel', 'translate']]]
            }
        ],
        keyboard: { bindTo: window }
    });
    console.log('[BpmnModeler] Modeler instance created');

    await loadDiagram(currentXML.value);
    console.log('[BpmnModeler] Diagram loaded');

    // Explicitly cast these as any to satisfy TS
    const eventBus = modeler.get('eventBus') as any;
    const selection = modeler.get('selection') as any;

    // Selection changed event
    eventBus.on('selection.changed', (e: any) => {
        const selectedElements = selection.get();
        if (selectedElements.length === 1) {
            const element = selectedElements[0];
            emit('nodeSelected', {
                id: element.id,
                type: element.type,
                businessObject: element.businessObject
            });
        } else {
            emit('nodeSelected', null);
        }
    });

    // Mark changes as unsaved
    eventBus.on(['elements.changed', 'elements.delete', 'connect.end', 'shape.move.end'], () => {
        emit('model-changed');
    });

    // Emit nodeDeleted when a node is deleted
    eventBus.on('commandStack.elements.delete.postExecuted', (event: any) => {
        const context = event.context;
        if (context && context.elements) {
            context.elements.forEach((element: any) => {
                if (element.id) {
                    emit('nodeDeleted', { id: element.id });
                }
            });
        }
        emit('model-changed');
    });
});

const factoryReset = async () => {
    if (confirm('Are you sure you want to reset to factory defaults?')) {
        try {
            const response = await props.processorService.getDefaultProcessModelXml();
            if (modeler) {
                await loadDiagram(response.processModelXml ?? '');
                currentXML.value = response.processModelXml ?? '';
                emit('factory-reset'); 
            }
        } catch (error) {
            console.error('Error performing factory reset:', error);
        }
    }
};

const discardChanges = async () => {
    if (confirm('Are you sure you want to discard all unsaved changes?')) {
        try {
            if (modeler) {
                await loadDiagram(currentXML.value);
                emit('save');
            }
        } catch (error) {
            console.error('Error discarding changes:', error);
        }
    }
};

const onSaveClicked = async () => {
  if (!modeler) return;

  try {
    // 1) Export new XML from the diagram
    const { xml } = await modeler.saveXML({ format: true });

    // 2) Save to server
    await props.processorService.setProcessModelXml(xml);

    // 3) Finally emit the 'save' event to tell the parent we are done
    emit('save');
  } catch (error) {
    console.error("Error saving BPMN:", error);
  }
}; 

/**
 * Provide a way for the parent component to get the current BPMN XML
 */
async function exportDiagram(): Promise<string | undefined> {
    if (!modeler) return '';
    const { xml } = await modeler.saveXML({ format: true });
    return xml;
}

__expose({ exportDiagram });

onBeforeUnmount(() => {
    if (modeler) {
        modeler.destroy();
    }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      ref_key: "canvas",
      ref: canvas,
      class: "bpmn-canvas"
    }, null, 512),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("button", {
        onClick: factoryReset,
        class: _normalizeClass(["factory-reset-button", { 'button-disabled': !canFactoryReset.value }]),
        title: "Reset to factory default"
      }, " Factory Reset ", 2),
      _createElementVNode("button", {
        onClick: discardChanges,
        class: _normalizeClass(["discard-button", { 'button-disabled': !__props.hasUnsavedChanges }]),
        disabled: !__props.hasUnsavedChanges,
        title: "Discard unsaved changes"
      }, " Discard Changes ", 10, _hoisted_3),
      _createElementVNode("button", {
        onClick: onSaveClicked,
        class: _normalizeClass(["save-button", { 'button-disabled': !__props.hasUnsavedChanges }]),
        disabled: !__props.hasUnsavedChanges,
        title: "Save changes"
      }, " Save ", 10, _hoisted_4)
    ])
  ]))
}
}

})